/**
 * 接通节点（Reroute）—— 纯逻辑。
 *
 * 它只有一个作用：**布线**。一根线从 A 拉到 B 太乱时，在中间放个点，
 * 变成 A → ● → B，两段各自可以绕开别的东西。
 *
 * 两件事必须做对，而且都容易漏：
 *
 *   1. **创建时把一条边拆成两段**，位置落在双击的那个点上
 *   2. **删除时把上下游接回去** —— 而且能穿过**一串**一起被删掉的接通节点：
 *
 *          A → ● → ● → B      同时删两个 → A → B
 *
 *      只处理「上游 + 下游」的单层是不够的，那样会留一条指向已删节点的边。
 */
import { sameHandle } from './connectionRules'
import type { EdgeLike } from './connectionRules'
import { edgeIdOfEndpoints } from './edgeOps'

/** 接通节点的两个端口 id */
export const REROUTE_IN = 'in'
export const REROUTE_OUT = 'out'

/** 拆边只需要这几个字段 */
export interface SplittableEdge {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}

export interface EdgeEndpoints {
  source: string
  sourceHandle: string | null
  target: string
  targetHandle: string | null
}

/**
 * 把一条边拆成经过接通节点的两段。
 *
 * 上游那段保留原来的 sourceHandle，下游那段保留原来的 targetHandle ——
 * 这样拆完之后**两端的端口类型校验结果不变**，不需要重新判定。
 */
export function splitEdgeForReroute(
  edge: SplittableEdge,
  rerouteId: string,
): { upstream: EdgeEndpoints; downstream: EdgeEndpoints } {
  return {
    upstream: {
      source: edge.source,
      sourceHandle: edge.sourceHandle ?? null,
      target: rerouteId,
      targetHandle: REROUTE_IN,
    },
    downstream: {
      source: rerouteId,
      sourceHandle: REROUTE_OUT,
      target: edge.target,
      targetHandle: edge.targetHandle ?? null,
    },
  }
}

/**
 * 边 id 的统一拼法。
 *
 * ⚠️ **实现只有一处**（`edgeOps.edgeIdOfEndpoints`）—— 原来这条约定在
 * 五个文件里各写了一遍。这个名字留着：改接和剪线都在用它。
 */
export function edgeIdOf(edge: EdgeEndpoints | SplittableEdge): string {
  return edgeIdOfEndpoints(edge)
}

/** 谁连到了这个接通节点的输入 */
function incomingOf(
  edges: readonly EdgeLike[],
  rerouteId: string,
): EdgeLike | undefined {
  return edges.find(
    (edge) =>
      !edge.muted &&
      edge.target === rerouteId &&
      sameHandle(edge.targetHandle, REROUTE_IN),
  )
}

/** 这个接通节点的输出连到了谁 */
function outgoingOf(
  edges: readonly EdgeLike[],
  rerouteId: string,
): EdgeLike | undefined {
  return edges.find(
    (edge) =>
      !edge.muted &&
      edge.source === rerouteId &&
      sameHandle(edge.sourceHandle, REROUTE_OUT),
  )
}

/** 顺着上游一路穿过「也被删掉」的接通节点，找到真正的来源 */
function resolveUpstream(
  edges: readonly EdgeLike[],
  startId: string,
  removed: ReadonlySet<string>,
  seen: Set<string>,
): { nodeId: string; handleId: string | null } | null {
  if (seen.has(startId)) return null // 防环
  seen.add(startId)

  const incoming = incomingOf(edges, startId)
  if (!incoming) return null
  if (removed.has(incoming.source)) {
    return resolveUpstream(edges, incoming.source, removed, seen)
  }
  return { nodeId: incoming.source, handleId: incoming.sourceHandle ?? null }
}

/** 顺着下游一路穿过「也被删掉」的接通节点，找到真正的去向 */
function resolveDownstream(
  edges: readonly EdgeLike[],
  startId: string,
  removed: ReadonlySet<string>,
  seen: Set<string>,
): { nodeId: string; handleId: string | null } | null {
  if (seen.has(startId)) return null
  seen.add(startId)

  const outgoing = outgoingOf(edges, startId)
  if (!outgoing) return null
  if (removed.has(outgoing.target)) {
    return resolveDownstream(edges, outgoing.target, removed, seen)
  }
  return { nodeId: outgoing.target, handleId: outgoing.targetHandle ?? null }
}

/**
 * 删掉这些接通节点之后，应该补出哪些边。
 *
 * 返回的边**不包含 id** —— id 由调用方按自己的规则生成（我们统一用
 * `e:source.handle->target.handle`），这里只负责「接谁到谁」。
 *
 * 只有上下游**都接得上**时才补边：一侧空着的接通节点直接删掉就行，
 * 硬接会把一个悬空端口接到别处去。
 */
export function reconnectAroundReroutes(
  edges: readonly EdgeLike[],
  removed: ReadonlySet<string>,
): EdgeEndpoints[] {
  const out: EdgeEndpoints[] = []
  const seen = new Set<string>()

  for (const id of removed) {
    const upstream = resolveUpstream(edges, id, removed, new Set())
    const downstream = resolveDownstream(edges, id, removed, new Set())
    if (!upstream || !downstream) continue

    const candidate: EdgeEndpoints = {
      source: upstream.nodeId,
      sourceHandle: upstream.handleId,
      target: downstream.nodeId,
      targetHandle: downstream.handleId,
    }

    const key = edgeIdOf(candidate)
    // 一串接通节点会各自算出同一条边，去重
    if (seen.has(key)) continue
    seen.add(key)
    out.push(candidate)
  }

  return out
}
